import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AdditionalPayment from './steps/AdditionalPayment';
import AdjustMonthlyPayment from './steps/AdjustMonthlyPayment';
import AcceptNewLoanAgreement from './steps/AcceptNewLoanAgreement';
import ConfirmPayment from './steps/ConfirmPayment';
import PaymentProcessing from './steps/PaymentProcessing';
import LoadingMessage from '../statusMessages/LoadingMessage';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Card, CardBody } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { getClientDataForPartialRedemption } from '../../actions/leadActions';
import StepError from '../acceptance/StepError';
import { strings } from '../../constants/localization';
import {
  ClientPortalNewPaymentDetailProps,
  SundhedClientPortalCreditProps,
  SundhedClientPortalCreditPartialRedeptionProps,
  SundhedClientPortalCreditFacilityData,
  SundhedClientPortalClientData,
  SundhedClientPortalCreditCardData,
} from 'components/interfaces';
import useClientPortalCorrectInterestRate from '../../hooks/useClientPortalCorrectInterestRate';

type PaymentFlowProps = {
  isClientPortal?: boolean;
  hasLeadUuid?: string;
  hasCreditFacilityNumber?: string;
  deviceType?: string;
  closeModal?: () => void;
  isSettlement?: boolean;
  numberOfReepayInvoicesToday: number;
};

const PaymentFlow: React.FC<PaymentFlowProps> = ({
  isClientPortal,
  hasLeadUuid,
  numberOfReepayInvoicesToday,
  hasCreditFacilityNumber,
  deviceType,
  closeModal,
  isSettlement,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [prevStep, setPrevStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentIsLoading, setPaymentIsLoading] = useState<boolean>(false);
  const [additionalPayment, setAdditionalPayment] = useState<string>('');
  const [hasRedirect, setHasRedirect] = useState<boolean>(false);
  const [source, setSource] = useState<string>('');
  const [device, setDevice] = useState<string>('');

  const [leadUuid, setLeadUuid] = useState<string>('');
  const [creditFacilityNumber, setCreditFacilityNumber] = useState<string>('');
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState<boolean>(false);
  const [newPaymentDetails, setNewPaymentDetails] =
    useState<ClientPortalNewPaymentDetailProps>();

  const dispatch = useDispatch();
  const finalInterestRate = useClientPortalCorrectInterestRate();

  const creditInformation = useSelector(
    (state: {
      memberPortal: { clientDataInformation: SundhedClientPortalCreditProps };
    }) => state.memberPortal?.clientDataInformation
  );
  const creditInformationPartialRedemption = useSelector(
    (state: {
      lead: {
        clientDataForPartialRedemption: SundhedClientPortalCreditPartialRedeptionProps;
      };
    }) => state.lead?.clientDataForPartialRedemption
  );
  const { clientData, creditFacilityData } = creditInformation || {};

  const [clientDetails, setClientDetails] =
    useState<SundhedClientPortalClientData>(clientData);
  const [creditFacilityDetails, setCreditFacilityDetails] =
    useState<SundhedClientPortalCreditFacilityData>(creditFacilityData);
  const [creditCardDetails, setCreditCardDetails] =
    useState<SundhedClientPortalCreditCardData>(
      creditFacilityData?.creditCardData
    );
  const [agreementType, setAgreementType] = useState<string>('invoice');

  useEffect(() => {
    if (isSettlement) {
      setCurrentStep(4);
    }
  }, [isSettlement]);

  // Helper function to extract query parameters
  const extractUrlParam = (param: string): string | null => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get(param);
  };

  const fetchClientData = async (uuid: string, facilityNumber: string) => {
    try {
      const res = await dispatch(
        getClientDataForPartialRedemption(uuid, facilityNumber)
      );

      if (res.status === 200) {
        setIsLoading(false);
      } else {
        setCurrentStep(null);
      }
    } catch (error) {
      console.log('error', error);
      setCurrentStep(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = () => {
      setIsLoading(true);

      const leadUuidFromUrl = extractUrlParam('leadUuid');
      const creditFacilityFromUrl = extractUrlParam('creditFacilityNumber');

      const sourceFromUrl = extractUrlParam('source') || deviceType || 'portal';
      const deviceFromUrl =
        extractUrlParam('device') || deviceType || 'desktop';

      if (sourceFromUrl) setSource(sourceFromUrl);
      if (deviceFromUrl) setDevice(deviceFromUrl);

      const uuid = leadUuidFromUrl || hasLeadUuid;
      const facilityNumber = creditFacilityFromUrl || hasCreditFacilityNumber;
      if (uuid && facilityNumber) {
        setLeadUuid(uuid);
        setCreditFacilityNumber(facilityNumber);
        setAgreementType('restructuring');
        fetchClientData(uuid, facilityNumber);
      } else {
        setIsLoading(false);
      }
    };

    initializeData();
  }, []);

  // Logic for managing hasLogo and hasRedirect
  useEffect(() => {
    if (source === 'portal' && device === 'mobile') {
      setHasRedirect(true);
    } else if (source === 'portal' && device === 'desktop') {
      setHasRedirect(false);
    } else {
      setHasRedirect(false);
    }
  }, [source, device]);

  useEffect(() => {
    if (hasRedirect) {
      setClientDetails(creditInformationPartialRedemption?.clientData);
      setCreditFacilityDetails(
        creditInformationPartialRedemption?.creditFacilityData
      );
      setCreditCardDetails(creditInformationPartialRedemption?.creditCard);
    }
  }, [hasRedirect, creditInformationPartialRedemption]);

  // Store previous step before the state change
  const handleNextStep = () => {
    setPrevStep(currentStep); // Capture current step as previous step
    setCurrentStep((prevStep) => prevStep + 1);
    window.parent.postMessage({ step: 2 }, '*');
  };

  const handlePreviousStep = () => {
    if (isSettlement) {
      closeModal && closeModal();
    } else {
      setPrevStep(currentStep); // Capture current step as previous step
      setCurrentStep((prevStep) => prevStep - 1);
      window.parent.postMessage({ step: 1 }, '*');
    }
  };

  // detect if the user is using an iOS device, then prevent the zoom in/out
  useEffect(() => {
    const iOS =
      !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    if (iOS) {
      document.addEventListener(
        'gesturestart',
        function (e) {
          e.preventDefault();
        },
        false
      );
    }
    if (!isClientPortal) {
      document.body.classList.add('public-content');
    } else {
      document.body.classList.add('private-content');
    }
    document.body.classList.add('public-flow');
    return () => {
      if (!isClientPortal) {
        document.body.classList.remove('public-flow');
      } else {
        document.body.classList.add('private-content');
      }
      document.body.classList.remove('public-content');
    };
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <AdditionalPayment
            onNextStep={handleNextStep}
            setAdditionalPayment={setAdditionalPayment}
            additionalPayment={additionalPayment}
            remainingBalance={creditFacilityDetails?.remainingBalance}
            hasCancelButton={source === 'portal' ? true : false}
            hasRedirect={hasRedirect}
            closeModal={closeModal}
          />
        );
      case 2:
        return (
          <AdjustMonthlyPayment
            onNextStep={handleNextStep}
            additionalPayment={additionalPayment}
            setNewPaymentDetails={setNewPaymentDetails}
            handlePreviousStep={handlePreviousStep}
            newPaymentDetails={newPaymentDetails}
            finalInterestRate={finalInterestRate}
            creditFacilityDetails={creditFacilityDetails}
          />
        );
      case 3:
        return (
          <AcceptNewLoanAgreement
            onNextStep={handleNextStep}
            setIsLoading={setIsLoading}
            newPaymentDetails={newPaymentDetails}
            extraPayment={additionalPayment}
            setNewPaymentDetails={setNewPaymentDetails}
            handlePreviousStep={handlePreviousStep}
            agreementType={agreementType}
            finalInterestRate={finalInterestRate}
            clientDetails={clientDetails}
            creditFacilityDetails={creditFacilityDetails}
          />
        );
      case 4:
        return (
          <ConfirmPayment
            onNextStep={handleNextStep}
            setPaymentIsLoading={setPaymentIsLoading}
            newPaymentDetails={newPaymentDetails}
            handlePreviousStep={handlePreviousStep}
            setIsPaymentConfirmed={setIsPaymentConfirmed}
            isSettlement={isSettlement}
            clientDetails={clientDetails}
            creditFacilityDetails={creditFacilityDetails}
            creditCardDetails={creditCardDetails}
          />
        );
      case 5:
        return (
          <PaymentProcessing
            additionalPayment={additionalPayment}
            newPaymentDetails={newPaymentDetails}
            isPaymentConfirmed={isPaymentConfirmed}
            leadUuid={clientDetails?.leadUuid || leadUuid}
            creditFacilityNumber={
              creditFacilityDetails?.number || creditFacilityNumber
            }
            hasCancelButton={source === 'portal' ? true : false}
            hasRedirect={hasRedirect}
            closeModal={closeModal}
            isSettlement={isSettlement}
            numberOfReepayInvoicesToday={
              numberOfReepayInvoicesToday ||
              creditFacilityDetails?.numberOfReepayInvoicesToday
            }
            creditFacilityDetails={creditFacilityDetails}
          />
        );
      default:
        return <StepError hasRedirect={hasRedirect} />;
    }
  };

  const directionClassName = currentStep > prevStep ? 'move-next' : 'move-prev';

  return (
    <>
      <div id='acceptance-flow' className='flow-wrapper'>
        {isLoading ? (
          <LoadingMessage />
        ) : (
          <TransitionGroup
            appear
            timeout={600}
            className={`slide-group ${directionClassName} step-${currentStep} ${
              paymentIsLoading ? 'loading-payment' : ''
            }`}
          >
            <CSSTransition
              key={currentStep}
              appear
              timeout={300}
              classNames='slide'
              className='transition-container'
            >
              <div className='sundhed-wrapper'>
                <Card
                  className={`flow-container ${
                    currentStep === 1
                      ? 'additional-payment'
                      : currentStep === 5
                      ? 'payment-processing'
                      : 'partial-redemption-flow'
                  }`}
                >
                  <CardBody>
                    {paymentIsLoading ? (
                      <LoadingMessage message={strings.loadingMessage} />
                    ) : (
                      renderStep()
                    )}
                  </CardBody>
                </Card>
              </div>
            </CSSTransition>
          </TransitionGroup>
        )}
      </div>
    </>
  );
};

export default PaymentFlow;
